// =============================================================================
// rooms.helper.js — Hotel detail room/offer pipeline (backend-driven)
// =============================================================================
//
// The hotel detail page renders one <RoomCard> per room. The BACKEND owns the
// full "what does each room show?" decision and emits it per room on the
// /hotel/gethotelwithprices payload:
//
//   room.displayOffers[]          – the offers to render (each with bookingMode)
//   room.isOpenGdsMatch           – room shows an OpenGds offer
//   room.hasAgreedPrice           – an L4B agreement applies to this room
//   room.showPriceAgreementBadge  – show the "L4B prijsafspraak" badge
//   room.sortRank                 – position in the rendered list
//
// This module simply maps those fields onto the room shape <RoomCard> expects.
// It contains NO price-agreement display logic; the rules (offer selection,
// synthesis for zero-availability rooms, CTA/badge, sort) live in the backend
// OfferDisplayService (ClassifyRoom / Synthesize / AssignSortRanks).
// =============================================================================

/**
 * OpenGds offers from the API often have stayTotalInclusive=0 and
 * stayTotalExclusive=0. Backfill them from nightlyTotal × nights so
 * downstream code (offer-summary, reservation form, etc.) can show a
 * meaningful total.
 */
const patchStayTotal = (offer, nightsInt) => {
  const nightly = offer.nightlyTotal || 0;
  const stayTotal =
    offer.stayTotalInclusive > 0
      ? offer.stayTotalInclusive
      : nightly * nightsInt;
  return {
    ...offer,
    stayTotalInclusive: stayTotal,
    stayTotalExclusive:
      offer.stayTotalExclusive > 0 ? offer.stayTotalExclusive : stayTotal,
    nightlyBase: offer.nightlyBase > 0 ? offer.nightlyBase : nightly
  };
};

/**
 * Map the backend-provided display fields (`displayOffers`, `isOpenGdsMatch`,
 * `hasAgreedPrice`, `showPriceAgreementBadge`, and per-offer `bookingMode`)
 * onto the room shape the detail page renders. No decision logic runs here.
 */
const classifyRoomFromBackend = (room, nightsInt) => {
  const offers = (room.displayOffers || []).map((o) =>
    patchStayTotal(o, nightsInt)
  );
  const requestReservation =
    offers.length > 0 &&
    offers.every((o) => String(o.bookingMode).toLowerCase() === "request");

  return {
    ...room,
    offers,
    isOpenGdsMatch: !!room.isOpenGdsMatch,
    hasAgreedPrice: !!room.hasAgreedPrice,
    hasPriceAgreementLabel: !!room.showPriceAgreementBadge,
    requestReservation
  };
};

/**
 * Build the `availableRooms` array for the hotel detail page from the
 * backend-provided display fields, sorted by the backend `sortRank`.
 *
 * @param {object} hotel – /hotel/gethotelwithprices response.
 * @param {number|string} nights
 *
 * @returns {object[]} rooms ready to be passed to <RoomCard>, sorted.
 */
const buildAvailableRooms = (hotel, nights) => {
  if (!Array.isArray(hotel?.expediaRapidRooms)) return [];

  const nightsInt = parseInt(nights) || 1;

  const rooms = hotel.expediaRapidRooms.map((room) =>
    classifyRoomFromBackend(room, nightsInt)
  );

  return [...rooms].sort((a, b) => (a.sortRank ?? 0) - (b.sortRank ?? 0));
};

export { buildAvailableRooms };
